import React, { createContext, useContext, useReducer } from 'react';
import { useQuery } from 'react-query';
import { api } from '../services/api';

// Initial state
const initialState = {
  settings: null,
  exchangeRate: null,
  loading: false,
  error: null,
  // Legacy support for userEmail
  userEmail: localStorage.getItem('userEmail') || '',
};

// Action types
const actionTypes = {
  // Legacy actions (keeping for backward compatibility)
  SET_USER_EMAIL: 'SET_USER_EMAIL',
  ADD_SIMULATION: 'ADD_SIMULATION',
  
  // App actions
  SET_SETTINGS: 'SET_SETTINGS',
  SET_EXCHANGE_RATE: 'SET_EXCHANGE_RATE',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
};

// Reducer function
function appReducer(state, action) {
  switch (action.type) {
    // Legacy actions (keeping for backward compatibility)
    case actionTypes.SET_USER_EMAIL:
      localStorage.setItem('userEmail', action.payload);
      return {
        ...state,
        userEmail: action.payload,
      };
    
    case actionTypes.ADD_SIMULATION:
      return {
        ...state,
        // Keep for compatibility but not used in new system
      };
    
    case actionTypes.SET_SETTINGS:
      return {
        ...state,
        settings: action.payload,
      };
    
    case actionTypes.SET_EXCHANGE_RATE:
      return {
        ...state,
        exchangeRate: action.payload,
      };
    
    case actionTypes.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      };
    
    case actionTypes.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false,
      };
    
    case actionTypes.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };
    
    default:
      return state;
  }
}

// Create context
const AppContext = createContext();

// Context provider component
export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(appReducer, initialState);
  
  // Fetch site settings
  useQuery('siteSettings', api.getSiteSettings, {
    onSuccess: (data) => {
      dispatch({ type: actionTypes.SET_SETTINGS, payload: data });
    },
    onError: (error) => {
      console.error('Error fetching site settings:', error);
    },
  });
  
  // Fetch current exchange rate
  useQuery('exchangeRate', api.getCurrentExchangeRate, {
    onSuccess: (data) => {
      dispatch({ type: actionTypes.SET_EXCHANGE_RATE, payload: data.current_rate });
    },
    onError: (error) => {
      console.error('Error fetching exchange rate:', error);
    },
    refetchInterval: 30 * 60 * 1000, // Refetch every 30 minutes
  });
  
  // No longer fetching user simulations automatically since we don't have authentication
  
  // Action creators
  const actions = {
    // Legacy actions (keeping for backward compatibility)
    setUserEmail: (email) => {
      dispatch({ type: actionTypes.SET_USER_EMAIL, payload: email });
    },
    
    addSimulation: (simulation) => {
      dispatch({ type: actionTypes.ADD_SIMULATION, payload: simulation });
    },
    
    setLoading: (loading) => {
      dispatch({ type: actionTypes.SET_LOADING, payload: loading });
    },
    
    setError: (error) => {
      dispatch({ type: actionTypes.SET_ERROR, payload: error });
    },
    
    clearError: () => {
      dispatch({ type: actionTypes.CLEAR_ERROR });
    },
  };
  
  const contextValue = {
    state,
    actions,
  };
  
  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
}

// Custom hook to use the context
export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}

// Utility hooks - simplified for backward compatibility
export function useAuth() {
  const { state, actions } = useApp();
  
  return {
    // Legacy properties (for backward compatibility)
    userEmail: state.userEmail,
    setUserEmail: actions.setUserEmail,
    isLoggedIn: false, // Always false since we don't have authentication
    isAuthenticated: false,
    user: null,
    token: null,
  };
}

export function useSettings() {
  const { state } = useApp();
  return state.settings;
}

export function useExchangeRate() {
  const { state } = useApp();
  return state.exchangeRate;
}